// Type 1 font programs (PFA / PFB / the PDF /FontFile stream): the encoding, the Subrs and the CharStrings with their charstring interpreter.

import { baseGlyphName } from "./glyphlist";
import type { FontProgram, GlyphPath } from "./truetype";

function decrypt(d: Uint8Array, r: number, skip: number): Uint8Array {
    const out = new Uint8Array(Math.max(0, d.length - skip));
    for (let i = 0; i < d.length; i++) {
        const c = d[i]!;
        const p = c ^ (r >> 8);
        r = ((c + r) * 52845 + 22719) & 0xffff;
        if (i >= skip) out[i - skip] = p;
    }
    return out;
}

const text = (b: Uint8Array, s = 0, e = b.length): string => { let r = ""; for (let i = s; i < e; i++) r += String.fromCharCode(b[i]!); return r; };

export class Type1Font implements FontProgram {
    unitsPerEm = 1000;
    numGlyphs = 0;
    fontMatrix = [0.001, 0, 0, 0.001, 0, 0];
    /** code → glyph name from the font's own /Encoding ("StandardEncoding" resolves in the caller). */
    encoding: Array<string | undefined> | null = null;
    standardEncoding = false;
    private subrs: Uint8Array[] = [];
    private chars = new Map<string, Uint8Array>();
    private names: string[] = [];
    private lenIV = 4;
    private cache = new Map<string, { path: GlyphPath; width: number } | null>();

    constructor(data: Uint8Array) {
        // PFB: strip the 0x80 segment headers
        if (data[0] === 0x80) {
            const parts: Uint8Array[] = [];
            let p = 0;
            while (p + 6 <= data.length && data[p] === 0x80) {
                const t = data[p + 1]!;
                if (t === 3) break;
                const len = data[p + 2]! | (data[p + 3]! << 8) | (data[p + 4]! << 16) | (data[p + 5]! << 24);
                parts.push(data.subarray(p + 6, p + 6 + len));
                p += 6 + len;
            }
            const n = parts.reduce((s, x) => s + x.length, 0);
            const all = new Uint8Array(n);
            let o = 0;
            for (const x of parts) { all.set(x, o); o += x.length; }
            data = all;
        }
        const s = text(data);
        const eex = s.indexOf("eexec");
        const clear = eex >= 0 ? s.slice(0, eex) : s;
        const fm = /\/FontMatrix\s*\[([^\]]+)\]/.exec(clear);
        if (fm) { const v = fm[1]!.trim().split(/\s+/).map(Number); if (v.length === 6 && v.every((x) => !Number.isNaN(x))) { this.fontMatrix = v; this.unitsPerEm = Math.round(1 / (v[0]! || 0.001)); } }
        if (/\/Encoding\s+StandardEncoding/.test(clear)) this.standardEncoding = true;
        else {
            const enc: Array<string | undefined> = new Array(256);
            const re = /dup\s+(\d+)\s*\/(\S+)\s+put/g;
            let m: RegExpExecArray | null;
            let any = false;
            while ((m = re.exec(clear))) { enc[+m[1]!] = m[2]; any = true; }
            if (any) this.encoding = enc;
        }
        if (eex < 0) return;
        let st = eex + 5;
        while (st < data.length && (data[st] === 32 || data[st] === 10 || data[st] === 13 || data[st] === 9)) st++;
        let body = data.subarray(st);
        // hex-encoded eexec section?
        let hex = true;
        for (let i = 0; i < 8; i++) { const c = body[i]!; if (!((c >= 0x30 && c <= 0x39) || (c >= 0x41 && c <= 0x46) || (c >= 0x61 && c <= 0x66) || c === 10 || c === 13 || c === 32)) hex = false; }
        if (hex) {
            const bytes: number[] = [];
            let hi = -1;
            for (const c of body) {
                const v = c >= 0x30 && c <= 0x39 ? c - 0x30 : c >= 0x41 && c <= 0x46 ? c - 0x37 : c >= 0x61 && c <= 0x66 ? c - 0x57 : -1;
                if (v < 0) continue;
                if (hi < 0) hi = v; else { bytes.push(hi * 16 + v); hi = -1; }
            }
            body = Uint8Array.from(bytes);
        }
        const priv = decrypt(body, 55665, 4);
        const ps = text(priv);
        const lv = /\/lenIV\s+(\d+)/.exec(ps);
        if (lv) this.lenIV = +lv[1]!;
        // Subrs: "dup i len RD <bytes> NP"
        const sIdx = ps.indexOf("/Subrs");
        const cIdx = ps.indexOf("/CharStrings");
        const readBin = (pos: number): { bytes: Uint8Array; end: number } | null => {
            // after "len RD " the next len bytes
            return { bytes: priv.subarray(pos), end: pos };
        };
        void readBin;
        if (sIdx >= 0) {
            const re = /dup\s+(\d+)\s+(\d+)\s+(?:RD|-\|)\s/g;
            re.lastIndex = sIdx;
            let m: RegExpExecArray | null;
            while ((m = re.exec(ps)) && (cIdx < 0 || m.index < cIdx)) {
                const idx = +m[1]!, len = +m[2]!;
                const start = m.index + m[0].length;
                this.subrs[idx] = decrypt(priv.subarray(start, start + len), 4330, this.lenIV);
                re.lastIndex = start + len;
            }
        }
        if (cIdx >= 0) {
            const re = /\/(\S+)\s+(\d+)\s+(?:RD|-\|)\s/g;
            re.lastIndex = cIdx + 12;
            let m: RegExpExecArray | null;
            while ((m = re.exec(ps))) {
                const len = +m[2]!;
                const start = m.index + m[0].length;
                this.chars.set(m[1]!, decrypt(priv.subarray(start, start + len), 4330, this.lenIV));
                this.names.push(m[1]!);
                re.lastIndex = start + len;
            }
        }
        this.numGlyphs = this.names.length;
    }

    gidByName(name: string): number { return this.names.indexOf(name); }
    gidByUnicode(_u: number): number { return 0; }
    nameOf(gid: number): string { return this.names[gid] ?? ""; }
    hasName(name: string): boolean { return this.chars.has(name); }
    /** The glyph name for a code from the font's own encoding. */
    nameForCode(code: number): string | undefined {
        if (this.encoding) return this.encoding[code];
        if (this.standardEncoding) return baseGlyphName("StandardEncoding", code) || undefined;
        return undefined;
    }
    advance(gid: number): number { return this.byName(this.names[gid] ?? "")?.width ?? -1; }
    glyph(gid: number): GlyphPath | null { return this.byName(this.names[gid] ?? "")?.path ?? null; }
    glyphByName(name: string): GlyphPath | null { return this.byName(name)?.path ?? null; }
    widthByName(name: string): number { return this.byName(name)?.width ?? -1; }

    private byName(name: string): { path: GlyphPath; width: number } | null {
        if (this.cache.has(name)) return this.cache.get(name)!;
        const cs = this.chars.get(name);
        let r: { path: GlyphPath; width: number } | null = null;
        if (cs) { try { r = this.run(cs, 0); } catch { r = null; } }
        this.cache.set(name, r);
        return r;
    }

    private run(code: Uint8Array, depth: number): { path: GlyphPath; width: number } {
        const path: GlyphPath = [];
        const stack: number[] = [];
        const ps: number[] = [];
        let x = 0, y = 0, sbx = 0, width = 0, open = false;
        let flex = false;
        const flexPts: number[] = [];
        let done = false;
        const moveTo = (dx: number, dy: number) => {
            x += dx; y += dy;
            if (flex) { flexPts.push(x, y); return; }
            if (open) path.push(4);
            path.push(0, x, y);
            open = true;
        };
        const lineTo = (dx: number, dy: number) => { x += dx; y += dy; path.push(1, x, y); };
        const curveTo = (a: number, b: number, c: number, d: number, e: number, f: number) => { const x1 = x + a, y1 = y + b, x2 = x1 + c, y2 = y1 + d; x = x2 + e; y = y2 + f; path.push(3, x1, y1, x2, y2, x, y); };
        const exec = (cs: Uint8Array, d: number) => {
            if (d > 12) return;
            let i = 0;
            while (i < cs.length && !done) {
                const b = cs[i++]!;
                if (b >= 32 && b <= 246) stack.push(b - 139);
                else if (b >= 247 && b <= 250) stack.push((b - 247) * 256 + cs[i++]! + 108);
                else if (b >= 251 && b <= 254) stack.push(-(b - 251) * 256 - cs[i++]! - 108);
                else if (b === 255) { stack.push((cs[i]! << 24) | (cs[i + 1]! << 16) | (cs[i + 2]! << 8) | cs[i + 3]!); i += 4; }
                else switch (b) {
                    case 1: case 3: stack.length = 0; break;
                    case 4: moveTo(0, stack.pop() ?? 0); stack.length = 0; break;
                    case 5: lineTo(stack[0] ?? 0, stack[1] ?? 0); stack.length = 0; break;
                    case 6: lineTo(stack[0] ?? 0, 0); stack.length = 0; break;
                    case 7: lineTo(0, stack[0] ?? 0); stack.length = 0; break;
                    case 8: curveTo(stack[0]!, stack[1]!, stack[2]!, stack[3]!, stack[4]!, stack[5]!); stack.length = 0; break;
                    case 9: if (open) { path.push(4); open = false; } stack.length = 0; break;
                    case 10: { const n = stack.pop()!; const s = this.subrs[n]; if (s) exec(s, d + 1); break; }
                    case 11: return;
                    case 13: sbx = stack[0] ?? 0; width = stack[1] ?? 0; x = sbx; y = 0; stack.length = 0; break;
                    case 14: if (open) { path.push(4); open = false; } done = true; return;
                    case 21: moveTo(stack[0] ?? 0, stack[1] ?? 0); stack.length = 0; break;
                    case 22: moveTo(stack[0] ?? 0, 0); stack.length = 0; break;
                    case 30: curveTo(0, stack[0]!, stack[1]!, stack[2]!, stack[3]!, 0); stack.length = 0; break;
                    case 31: curveTo(stack[0]!, 0, stack[1]!, stack[2]!, 0, stack[3]!); stack.length = 0; break;
                    case 12: {
                        const e = cs[i++]!;
                        switch (e) {
                            case 0: case 1: case 2: stack.length = 0; break;
                            case 6: {
                                // seac: asb adx ady bchar achar
                                const [asb, adx, ady, bch, ach] = stack as [number, number, number, number, number];
                                stack.length = 0;
                                const nm = (c: number) => baseGlyphName("StandardEncoding", c);
                                const compose = (name: string, ox: number, oy: number) => {
                                    const g = this.chars.get(name);
                                    if (!g || depth > 2) return;
                                    const r = this.run(g, depth + 1).path;
                                    let k = 0;
                                    while (k < r.length) { const op = r[k++]!; path.push(op); const n = op === 0 || op === 1 ? 1 : op === 2 ? 2 : op === 3 ? 3 : 0; for (let q = 0; q < n; q++) path.push(r[k++]! + ox, r[k++]! + oy); }
                                };
                                if (open) { path.push(4); open = false; }
                                compose(nm(bch), 0, 0);
                                compose(nm(ach), sbx - asb + adx, ady);
                                done = true;
                                return;
                            }
                            case 7: sbx = stack[0] ?? 0; width = stack[2] ?? 0; x = sbx; y = stack[1] ?? 0; stack.length = 0; break;
                            case 12: { const b2 = stack.pop()!, a = stack.pop()!; stack.push(b2 ? a / b2 : 0); break; }
                            case 16: {
                                const n = stack.pop()!, nargs = stack.pop()!;
                                const args: number[] = [];
                                for (let k = 0; k < nargs; k++) args.push(stack.pop()!);
                                if (n === 1) { flex = true; flexPts.length = 0; }
                                else if (n === 2) { /* a flex point was recorded by the rmoveto */ }
                                else if (n === 0) {
                                    flex = false;
                                    if (flexPts.length >= 14) {
                                        const p = flexPts;
                                        // p: reference point, then six points; curves go through points 2..7 relative to the start
                                        path.push(3, p[2]!, p[3]!, p[4]!, p[5]!, p[6]!, p[7]!, 3, p[8]!, p[9]!, p[10]!, p[11]!, p[12]!, p[13]!);
                                        x = p[12]!; y = p[13]!;
                                    }
                                    ps.push(y, x);
                                } else if (n === 3) ps.push(args[0] ?? 3);
                                else for (const a of args.reverse()) ps.push(a);
                                break;
                            }
                            case 17: stack.push(ps.pop() ?? 0); break;
                            case 33: x = stack[0] ?? x; y = stack[1] ?? y; stack.length = 0; break;
                            default: stack.length = 0;
                        }
                        break;
                    }
                    default: stack.length = 0;
                }
            }
        };
        exec(code, 0);
        if (open) path.push(4);
        return { path, width };
    }
}
